import { useOutletContext } from 'react-router-dom';
import { Card } from '@aimsos/ui';
import { Trans, useTranslation } from '@aimsos/i18n';
import { StatusPill } from '../../components';
import { formatRelativeTime } from '../../utils';

/**
 * Instance Detail — Basic Info tab (CT-4188). Prototype `instanceBasicHTML`:
 * health snapshot + about copy on the left, configuration sidebar on the right.
 */
const BasicInfoTab = () => {
  const { t } = useTranslation('admin-studio');
  const { instance } = useOutletContext();
  const total = instance.instanceCount ?? 1;
  const errors = instance.errors24h ?? 0;

  const configRows = [
    { key: 'instanceId', label: t('integrations.instance.basic.instanceId'), value: instance.instanceId, mono: true },
    { key: 'displayName', label: t('integrations.instance.basic.displayName'), value: instance.name },
    {
      key: 'authMode',
      label: t('integrations.instance.basic.authMode'),
      value: t(`integrations.instance.basic.authModeValue.${instance.managedBy}`),
    },
    {
      key: 'authMethod',
      label: t('integrations.instance.basic.authMethod'),
      value: t(`integrations.provider.auth.${instance.authMethod}`),
    },
    {
      key: 'activatedBy',
      label: t('integrations.instance.basic.activatedBy'),
      value: `${instance.connectedBy?.name ?? '—'} · ${formatRelativeTime(instance.connectedAt, t) || '—'}`,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="flex min-w-0 flex-col gap-3.5">
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-[18px] pb-2.5 pt-3.5">
            <h3 className="text-sm font-semibold tracking-[-0.005em] text-content">
              {t('integrations.instance.basic.health')}
            </h3>
            <p className="shrink-0 text-[11px] font-medium text-content-faint">
              {t('integrations.instance.basic.last24h')}
            </p>
          </div>
          <Card.Body className="px-[18px] pb-[18px] pt-0">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              <div className="rounded-lg border border-edge bg-white/[0.03] px-3 py-3">
                <p className="mb-1.5 text-xs font-semibold tracking-[-0.005em] text-content">
                  {t('integrations.instance.basic.status')}
                </p>
                <StatusPill status={instance.status} surface="detail" />
              </div>
              <div className="rounded-lg border border-edge bg-white/[0.03] px-3 py-3">
                <p className="mb-1.5 text-xs font-semibold tracking-[-0.005em] text-content">
                  {t('integrations.instance.basic.lastRun')}
                </p>
                <p className="text-[11.5px] leading-snug text-content-secondary">
                  {formatRelativeTime(instance.lastRun, t) || '—'}
                </p>
              </div>
              <div className="rounded-lg border border-edge bg-white/[0.03] px-3 py-3">
                <p className="mb-1.5 text-xs font-semibold tracking-[-0.005em] text-content">
                  {t('integrations.instance.basic.errors24h')}
                </p>
                <p
                  className={[
                    'text-[11.5px] font-medium leading-snug',
                    errors > 0 ? 'text-[#EF4444]' : 'text-content',
                  ].join(' ')}
                >
                  {errors}
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-[18px] pb-2.5 pt-3.5">
            <h3 className="text-sm font-semibold tracking-[-0.005em] text-content">
              {t('integrations.instance.basic.about')}
            </h3>
            <p className="shrink-0 text-[11px] font-medium text-content-faint">
              {t(total === 1 ? 'integrations.instance.basic.ofOne' : 'integrations.instance.basic.ofMany', {
                total,
              })}
            </p>
          </div>
          <Card.Body className="px-[18px] pb-[18px] pt-0">
            <p className="text-[13px] leading-normal text-content-secondary">
              <Trans
                ns="admin-studio"
                i18nKey="integrations.instance.basic.aboutBody"
                values={{ count: total, name: instance.providerName }}
                components={{ strong: <strong className="font-semibold text-content" /> }}
              />
            </p>
          </Card.Body>
        </Card>
      </div>

      <aside className="min-w-0">
        <Card className="overflow-hidden lg:sticky lg:top-4">
          <div className="px-[18px] pb-1.5 pt-3.5">
            <h3 className="text-sm font-semibold tracking-[-0.005em] text-content">
              {t('integrations.instance.basic.configuration')}
            </h3>
          </div>
          <Card.Body className="px-[18px] pb-3.5 pt-0">
            <dl>
              {configRows.map((row, index) => (
                <div
                  key={row.key}
                  className={[
                    'flex flex-col gap-0.5 py-2.5',
                    index < configRows.length - 1 ? 'border-b border-edge' : '',
                  ].join(' ')}
                >
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-content-faint">
                    {row.label}
                  </dt>
                  <dd
                    className={[
                      'break-words text-xs text-content',
                      row.mono ? 'font-mono text-[11.5px]' : '',
                    ].join(' ')}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </Card.Body>
        </Card>
      </aside>
    </div>
  );
};

export default BasicInfoTab;
